import React, { useState, useEffect, useCallback, useRef } from 'react';
import { pqrService, handleApiError } from '../services/api';
import toast from 'react-hot-toast';

const CATEGORY_LABELS = {
  producto: '🔴 Producto (Calidad)',
  logistica: '🟠 Logística (Entrega)',
  facturacion: '🟡 Facturación / Pago',
  atencion: '🔵 Atención / Servicio',
};

const PRIORITY_OPTIONS = [
  { value: 'baja', label: '🟢 Baja' },
  { value: 'media', label: '🟡 Media' },
  { value: 'alta', label: '🟠 Alta' },
  { value: 'critica', label: '🔴 Crítica' },
];

const DELIVERY_LABELS = {
  pickup: 'Recogida en bodega',
  recoge_bodega: 'Recoge en bodega',
  recoge_posterior: 'Recoge posterior',
  local_messenger: 'Mensajero local',
  mensajeria_urbana: 'Mensajería urbana',
  mensajeria_local: 'Mensajería local',
  domicilio: 'Domicilio',
  national_carrier: 'Transportadora nacional',
  transportadora: 'Transportadora',
};

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function PQRCreateModal({ isOpen, onClose, onCreated, orderId: propOrderId, customerId: propCustomerId, orderNumber: propOrderNumber, source = 'manual' }) {
  const [subcategories, setSubcategories] = useState({});
  const [form, setForm] = useState({
    category: '', subcategory: '', description: '', priority: 'media',
    financial_impact: '', source,
  });
  const [loading, setLoading] = useState(false);

  // Order search state
  const [orderSearch, setOrderSearch] = useState('');
  const [orderResults, setOrderResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Evidence state (Ctrl+V paste)
  const [evidenceFiles, setEvidenceFiles] = useState([]); // File objects
  const [evidencePreviews, setEvidencePreviews] = useState([]); // data URLs
  const pasteZoneRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      pqrService.getSubcategories().then(r => {
        if (r?.data) setSubcategories(r.data);
      }).catch(() => {});
      setForm(f => ({ ...f, category: '', subcategory: '', description: '', priority: 'media', financial_impact: '', source }));
      setSelectedOrder(null);
      setOrderSearch('');
      setOrderResults([]);
      setEvidenceFiles([]);
      setEvidencePreviews([]);
    }
  }, [isOpen, source]);

  // Global paste listener when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file && evidenceFiles.length < 5) {
            addEvidenceFile(file);
          } else if (evidenceFiles.length >= 5) {
            toast.error('Máximo 5 evidencias');
          }
          break;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, evidenceFiles]);

  const addEvidenceFile = (file) => {
    setEvidenceFiles(prev => [...prev, file]);
    const reader = new FileReader();
    reader.onload = (e) => {
      setEvidencePreviews(prev => [...prev, e.target.result]);
    };
    reader.readAsDataURL(file);
  };

  const removeEvidence = (idx) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== idx));
    setEvidencePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // Debounced order search
  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setOrderResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      setSearchLoading(true);
      const res = await pqrService.searchOrders(q);
      setOrderResults(res?.data || []);
      setShowDropdown(true);
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setOrderSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 400);
  };

  const selectOrder = (order) => {
    setSelectedOrder(order);
    setOrderSearch(order.order_number);
    setShowDropdown(false);
  };

  const clearOrder = () => {
    setSelectedOrder(null);
    setOrderSearch('');
    setOrderResults([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const next = { ...f, [name]: value };
      if (name === 'category') next.subcategory = '';
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error('Selecciona una categoría');
    if (!form.description?.trim()) return toast.error('Describe el problema');
    try {
      setLoading(true);

      // Build FormData to support file uploads
      const fd = new FormData();
      fd.append('category', form.category);
      fd.append('subcategory', form.subcategory || '');
      fd.append('description', form.description);
      fd.append('priority', form.priority);
      fd.append('source', form.source || 'manual');
      fd.append('order_id', selectedOrder?.id || propOrderId || '');
      fd.append('customer_id', selectedOrder?.customer_id || propCustomerId || '');
      fd.append('delivery_method', selectedOrder?.delivery_method || '');
      fd.append('financial_impact', form.financial_impact ? parseFloat(form.financial_impact) : 0);

      // Append evidence files
      evidenceFiles.forEach(file => {
        fd.append('evidence', file, file.name || `evidence-${Date.now()}.png`);
      });

      const res = await pqrService.create(fd);
      if (res?.success) {
        toast.success(`PQR #${res.data?.id} creado exitosamente`);
        onCreated?.();
        onClose();
      }
    } catch (err) {
      toast.error(handleApiError(err, 'Error creando PQR'));
    } finally {
      setLoading(false);
    }
  };

  // File input for manual selection
  const fileInputRef = useRef(null);
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (evidenceFiles.length < 5 && file.type.startsWith('image/')) {
        addEvidenceFile(file);
      }
    });
    e.target.value = '';
  };

  if (!isOpen) return null;

  const currentSubs = subcategories[form.category] || [];
  const fmt = (v) => v != null ? `$${Number(v).toLocaleString('es-CO')}` : '-';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-4 rounded-t-xl">
          <h2 className="text-lg font-bold">📋 Nuevo PQR</h2>
          <p className="text-sm opacity-80">Vincula un pedido para registrar la novedad</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Buscador de Pedido */}
          <div className="relative" ref={searchRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">🔍 Buscar Pedido (Nº factura o cliente)</label>
            <div className="flex gap-2">
              <input type="text" value={orderSearch} onChange={handleSearchChange}
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                placeholder="Ej: FV-2-48000 o nombre del cliente"
                onFocus={() => orderResults.length > 0 && setShowDropdown(true)} />
              {selectedOrder && (
                <button type="button" onClick={clearOrder} className="px-2 text-gray-400 hover:text-red-500 text-lg">✕</button>
              )}
            </div>
            {searchLoading && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}

            {/* Dropdown de resultados */}
            {showDropdown && orderResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {orderResults.map(o => (
                  <button key={o.id} type="button" onClick={() => selectOrder(o)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-sm">
                    <span className="font-semibold text-blue-600">{o.order_number}</span>
                    <span className="text-gray-500 ml-2">• {o.customer_name}</span>
                    <span className="text-gray-400 ml-2">• {o.order_date}</span>
                    <span className="text-gray-500 ml-2">• {fmt(o.total)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tarjeta de Contexto del Pedido */}
          {selectedOrder && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-blue-800 text-sm">{selectedOrder.order_number}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-200 text-blue-800 capitalize">{selectedOrder.status?.replace(/_/g, ' ')}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                <div><span className="text-gray-400">Cliente:</span> <span className="font-medium">{selectedOrder.customer_name || '-'}</span></div>
                <div><span className="text-gray-400">NIT:</span> <span className="font-medium">{selectedOrder.customer_nit || '-'}</span></div>
                <div><span className="text-gray-400">Fecha:</span> <span className="font-medium">{selectedOrder.order_date}</span></div>
                <div><span className="text-gray-400">Total:</span> <span className="font-medium">{fmt(selectedOrder.total)}</span></div>
                <div><span className="text-gray-400">Envío:</span> <span className="font-medium">{DELIVERY_LABELS[selectedOrder.delivery_method] || selectedOrder.delivery_method || '-'}</span></div>
                <div><span className="text-gray-400">Transportadora:</span> <span className="font-medium">{selectedOrder.carrier_name || '-'}</span></div>
                {selectedOrder.messenger_name && (
                  <div><span className="text-gray-400">Mensajero:</span> <span className="font-medium">{selectedOrder.messenger_name}</span></div>
                )}
                {selectedOrder.tracking_number && (
                  <div><span className="text-gray-400">Guía:</span> <span className="font-medium">{selectedOrder.tracking_number}</span></div>
                )}
              </div>
            </div>
          )}

          {/* Categoría */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                <button key={val} type="button"
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                    form.category === val
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 font-semibold'
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                  onClick={() => handleChange({ target: { name: 'category', value: val } })}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Subcategoría */}
          {currentSubs.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de problema</label>
              <select name="subcategory" value={form.subcategory} onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">-- Seleccionar --</option>
                {currentSubs.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Prioridad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
            <select name="priority" value={form.priority} onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              {PRIORITY_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción del problema *</label>
            <textarea name="description" value={form.description} onChange={handleChange}
              rows={3} className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Describe el problema con detalle..." />
          </div>

          {/* Evidencia — Ctrl+V o selección de archivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">📎 Evidencia (Ctrl+V para pegar)</label>
            <div
              ref={pasteZoneRef}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
                evidenceFiles.length > 0
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              {evidenceFiles.length === 0 ? (
                <div className="text-gray-400 text-sm py-2">
                  <div className="text-2xl mb-1">📷</div>
                  <p><strong>Ctrl+V</strong> para pegar una captura</p>
                  <p className="text-xs mt-1">o clic aquí para seleccionar archivo</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 justify-center">
                  {evidencePreviews.map((src, i) => (
                    <div key={i} className="relative group">
                      <img src={src} alt={`Evidencia ${i + 1}`}
                        className="h-20 w-20 object-cover rounded-lg border shadow-sm" />
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeEvidence(i); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                        ✕
                      </button>
                    </div>
                  ))}
                  {evidenceFiles.length < 5 && (
                    <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-2xl">
                      +
                    </div>
                  )}
                </div>
              )}
            </div>
            <input type="file" ref={fileInputRef} accept="image/*" multiple
              onChange={handleFileSelect} className="hidden" />
            <p className="text-xs text-gray-400 mt-1">{evidenceFiles.length}/5 evidencias • Máx 15MB cada una</p>
          </div>

          {/* Impacto financiero */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Impacto financiero ($)</label>
            <input type="number" name="financial_impact" value={form.financial_impact} onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="0" />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50">
              {loading ? 'Creando...' : 'Crear PQR'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
